"use client";
import React, { useState, useRef, useMemo } from 'react';
import { Button } from "@/components/ui/button";
import { CircleCheckBig, ArrowUpRight, HelpCircle, ChevronDown } from 'lucide-react';
import { ICONS } from '@/lib/iconRegistry';
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useLivePaths } from "@/context/LiveNavContext";
import { resolveLiveHref, filterLiveLinks } from "@/lib/live-nav";

const HEADER_OFFSET = 96;

const isLinkFeature = (f) =>
  f !== null && typeof f === 'object' && !React.isValidElement(f) && typeof f.href === 'string';

function TabPanel({ item, href, livePaths, compact = false }) {
  const Icon = ICONS[item.iconName] || HelpCircle;
  const liveLinks = new Set(filterLiveLinks(item.features.filter(isLinkFeature), livePaths));
  const features = item.features.filter((f) => !isLinkFeature(f) || liveLinks.has(f));

  return (
    <>
      {compact ? (
        <h3 className="text-lg font-bold text-foreground leading-snug mb-3">{item.title}</h3>
      ) : (
        <div className="flex items-center gap-2.5 sm:gap-3 mb-4">
          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <Icon size={18} className="text-primary sm:hidden" />
            <Icon size={20} className="text-primary hidden sm:block" />
          </div>
          <h3 className="text-xl sm:text-2xl font-bold text-foreground leading-[1.15] sm:leading-[1.1]">
            {item.title.split(' ').map((word, i) => (
              <span key={i} className={["your", "workflow."].includes(word.toLowerCase()) ? "text-primary" : ""}>
                {word}{" "}
              </span>
            ))}
          </h3>
        </div>
      )}

      {Array.isArray(item.description)
        ? item.description.map((line, i) =>
          typeof line === 'string'
            ? <p key={i} className="text-sm sm:text-base text-muted-foreground mb-3 leading-relaxed">{line}</p>
            : <div key={i} className="text-sm sm:text-base text-muted-foreground mb-3 leading-relaxed">{line}</div>
        )
        : <p className="text-sm sm:text-base text-muted-foreground mb-5 leading-relaxed">
          {item.description}
        </p>
      }

      <div className={`grid grid-cols-1 sm:grid-cols-2 ${compact ? 'gap-2 mb-5' : 'gap-2.5 sm:gap-3 mb-6'}`}>
        {features.map((feature, idx) => {
          const cardClass = `text-sm flex items-start text-primary bg-white/50 hover:bg-primary/10 transition-colors duration-300 backdrop-blur-sm border border-border/30 rounded-lg ${compact ? 'p-2.5' : 'p-3 sm:p-4'}`;
          const check = <CircleCheckBig size={compact ? 16 : 18} strokeWidth={2} className="mr-3 shrink-0" />;

          if (!isLinkFeature(feature)) {
            return (
              <div key={idx} className={cardClass}>
                {check}
                <span className="font-semibold text-muted-foreground">{feature}</span>
              </div>
            );
          }

          return (
            <Link key={`${feature.href}-${idx}`} href={feature.href} className={`group ${cardClass}`}>
              {check}
              <span className="flex-1 font-semibold text-muted-foreground group-hover:text-foreground transition-colors duration-200">{feature.label}</span>
              <ArrowUpRight size={14} className="ml-2 mt-0.5 shrink-0 opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-200" />
            </Link>
          );
        })}
      </div>

      <div className='flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-6'>
        {href && item.linkText && (
          <Link href={href} className="w-full sm:w-auto">
            <Button size="lg" className="w-full sm:w-auto bg-red-gradient border-0 text-sm md:text-base py-5 px-4 btn-glass group cursor-pointer">
              <span className="relative z-10 text-sm flex items-center justify-center gap-2 text-white group-hover:text-secondary transition-colors duration-400">
                Explore Service
                <ArrowUpRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
              </span>
            </Button>
          </Link>
        )}

        {item.link && item.linkText && (
          <Link href="/contact-us" className="group flex items-center justify-center sm:justify-start gap-1.5 text-sm font-semibold text-secondary/80 hover:text-secondary transition-colors duration-200">
            {item.linkText}
            <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-200" />
          </Link>
        )}
      </div>
    </>
  );
}

function filterLiveTabs(items, livePaths) {
  return items.filter((item) => {
    const parentLive = !item.link || filterLiveLinks([{ href: `/${item.link}` }], livePaths).length > 0;
    if (parentLive) return true;
    if (!item.allowWithoutParent) return false;
    const links = (item.features ?? []).filter(isLinkFeature);
    return filterLiveLinks(links, livePaths).length > 0;
  });
}

const ServiceTab = ({ data: rawData, hideNonLiveTabs = false }) => {
  const livePaths = useLivePaths();
  const data = useMemo(
    () => (hideNonLiveTabs && rawData ? filterLiveTabs(rawData, livePaths) : rawData),
    [rawData, livePaths, hideNonLiveTabs]
  );
  const [activeId, setActiveId] = useState(data?.[0]?.id);
  const [mobileCollapsed, setMobileCollapsed] = useState(false);
  const rowRefs = useRef({});
  const pathname = usePathname();

  if (!data || data.length === 0) return null;

  const activeTab = data.find((item) => item.id === activeId) || data[0];
  const hrefFor = (item) => {
    if (!item.link) return null;
    const href = resolveLiveHref(`/${item.link}`, livePaths);
    return href === pathname ? null : href;
  };

  const selectTab = (item) => {
    if (item.id === activeTab.id) {
      setMobileCollapsed((c) => !c);
      return;
    }
    setActiveId(item.id);
    setMobileCollapsed(false);

    if (window.matchMedia('(max-width: 767px)').matches) {
      requestAnimationFrame(() => {
        const row = rowRefs.current[item.id];
        if (!row) return;
        const top = row.getBoundingClientRect().top;
        if (top < HEADER_OFFSET || top > window.innerHeight * 0.6) {
          window.scrollTo({ top: window.scrollY + top - HEADER_OFFSET, behavior: 'smooth' });
        }
      });
    }
  };

  return (
    <div className="relative w-full max-w-[1440px] mx-auto rounded-2xl overflow-hidden bg-white/60 backdrop-blur-md border border-white/80 shadow-lg flex flex-col md:flex-row">

      <div className="w-full md:w-72 bg-white/40 backdrop-blur-sm md:border-r border-border/30 flex flex-col shrink-0">
        {data.map((item) => {
          const Icon = ICONS[item.iconName] || HelpCircle;
          const isActive = activeTab.id === item.id;
          const isOpenOnMobile = isActive && !mobileCollapsed;
          const panelId = `service-panel-${item.id}`;

          return (
            <div key={item.id} className="border-b border-border/30 last:border-b-0 md:border-b-0">
              <button
                ref={(el) => { rowRefs.current[item.id] = el; }}
                type="button"
                onClick={() => selectTab(item)}
                aria-expanded={isOpenOnMobile}
                aria-controls={panelId}
                className={`w-full flex items-center gap-3 md:gap-4 p-3.5 md:p-3 text-left transition-all relative border-l-4 cursor-pointer
                  ${isActive
                    ? "bg-[#fddcdc]/40 border-primary md:shadow-sm"
                    : "bg-transparent border-transparent hover:bg-[#fddcdc]/40"
                  }`}
              >
                <div className={`p-2 rounded-lg shrink-0 ${isActive ? "bg-red-100 text-primary" : "bg-white/100 text-slate-500"}`}>
                  <Icon size={20} />
                </div>
                <p className={`flex-1 text-sm font-semibold ${isActive ? "text-foreground" : "text-muted-foreground"}`}>
                  {item.label}
                </p>
                <ChevronDown
                  size={18}
                  aria-hidden="true"
                  className={`md:hidden shrink-0 text-slate-400 transition-transform duration-300 ${isOpenOnMobile ? "rotate-180 text-primary" : ""}`}
                />
              </button>

              {isOpenOnMobile && (
                <div id={panelId} className="md:hidden px-4 pt-4 pb-5 bg-white/50 animate-in fade-in duration-300">
                  <TabPanel item={item} href={hrefFor(item)} livePaths={livePaths} compact />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* CONTENT AREA (desktop) */}
      <div className="hidden md:block flex-1 p-6 lg:p-10 animate-in fade-in slide-in-from-right-4 duration-500">
        <TabPanel item={activeTab} href={hrefFor(activeTab)} livePaths={livePaths} />
      </div>
    </div>
  );
};

export default ServiceTab;
